import React from 'react';

import { hockeyFormatter, stringFormatter } from '@nhl/formatter-utilities';

import GlobalColumnFunctions from '../../../../../../utils/globalColumnFunctions';

import BaseColumnDefinitions, { sumValues } from './baseColumnDefinitions';

const gmNameColumn = {
    Header: BaseColumnDefinitions.CreateHeaderCell('Name'),
    accessor: 'fullName',
    minWidth: 220,
    maxWidth: 300,
    headerClassName: 'left',
    className: 'left',
    Cell: ({ original }) => {
        const generalManagerId =
            original.generalManager?.id || original?.subRows?.[0]?.generalManager?.id || original.generalManagerId;
        return (
            <a
                href={`/general-managers/${generalManagerId}`}
                className={original.activeGm ? 'strong' : ''}
                rel="noopener"
                target="_blank"
            >
                {original.fullName || original.generalManagerName}
            </a>
        );
    },
};

const CONSECUTIVE_SEASONS = {
    accessor: 'consecutiveSeasons',
    Header: BaseColumnDefinitions.CreateHeaderCell('Consecutive Playoff Seasons', 'Consec. PS'),
    getProps: GlobalColumnFunctions.getProps,
};

const CUP_WINS_COL = {
    accessor: 'stanleyCups',
    Header: BaseColumnDefinitions.CreateHeaderCell('Stanley Cups Won', 'Cup Wins'),
    getProps: GlobalColumnFunctions.getProps,
};

const GAMES_MANAGED_COL = {
    accessor: 'games',
    aggregate: sumValues,
    Header: BaseColumnDefinitions.CreateHeaderCell('Games Managed', 'GM'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const GAMES_PLAYED_COL = {
    accessor: 'games',
    aggregate: sumValues,
    Header: BaseColumnDefinitions.CreateHeaderCell('Games Played', 'GP'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const FIRST_SEASON_COL = {
    accessor: 'startSeasonId',
    Header: BaseColumnDefinitions.CreateHeaderCell('First Season', 'First Season'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: (cellData) => hockeyFormatter.sluggedSeasonFormatter(cellData.value),
};

const FRANCHISE_COL = {
    accessor: 'franchiseName',
    Header: BaseColumnDefinitions.CreateHeaderCell('Franchise', 'Franchise'),
    getProps: GlobalColumnFunctions.getProps,
};

const LAST_SEASON_COL = {
    accessor: 'endSeasonId',
    Header: BaseColumnDefinitions.CreateHeaderCell('Last Season', 'Last Season'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: (cellData) => hockeyFormatter.sluggedSeasonFormatter(cellData.value),
};

const SEASONS_COL = {
    accessor: 'seasons',
    Header: BaseColumnDefinitions.CreateHeaderCell('Seasons', 'Seasons'),
    getProps: GlobalColumnFunctions.getProps,
};

const STANLEY_CUP_FINAL_APPEARANCES = {
    accessor: 'stanleyCupFinalAppearances',
    Header: BaseColumnDefinitions.CreateHeaderCell('Stanley Cup Final Appearances', 'Appearances'),
    getProps: GlobalColumnFunctions.getProps,
};

const TEAMS_COL = {
    accessor: 'teamAbbrevs',
    Header: BaseColumnDefinitions.CreateHeaderCell('Teams', 'Team(s)'),
    getProps: GlobalColumnFunctions.getProps,
};

const GAMES_WINS_COL = (isPlayoffs = false) => [
    GAMES_PLAYED_COL,
    BaseColumnDefinitions.WINS_COL,
    BaseColumnDefinitions.LOSSES_COL,
    BaseColumnDefinitions.TIES_COL,
    BaseColumnDefinitions.OVERTIME_LOSSES_COL,
    isPlayoffs ? CUP_WINS_COL : BaseColumnDefinitions.POINTS_COL,
];

const GM_CAREER_RECORD_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    { ...gmNameColumn, Header: BaseColumnDefinitions.CreateHeaderCell('General Manager') },
    TEAMS_COL,
    GAMES_MANAGED_COL,
    SEASONS_COL,
    FIRST_SEASON_COL,
    LAST_SEASON_COL,
];

const SEASONS_AND_CUPS_COLS = [FIRST_SEASON_COL, LAST_SEASON_COL, CUP_WINS_COL];

const GM_GAMES_SEASONS_CUPS_COLS = [GAMES_MANAGED_COL, SEASONS_COL, ...SEASONS_AND_CUPS_COLS];

const GM_PLAYOFF_CAREER_BASE_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    { ...gmNameColumn, Header: BaseColumnDefinitions.CreateHeaderCell('General Manager', 'General Manager') },
    BaseColumnDefinitions.TEAM_ABBREVS,
];

const GM_PLAYOFF_FRANCHISE_BASE_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    { ...gmNameColumn, Header: BaseColumnDefinitions.CreateHeaderCell('General Manager', 'General Manager') },
    FRANCHISE_COL,
];

const GM_SINGLE_FRANCHISE_RECORD_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    { ...gmNameColumn, Header: BaseColumnDefinitions.CreateHeaderCell('General Manager', 'General Manager') },
    FRANCHISE_COL,
    GAMES_MANAGED_COL,
    SEASONS_COL,
    FIRST_SEASON_COL,
    LAST_SEASON_COL,
];

export const generalManagerColumnDefinitions = {
    // Wins
    'gm-wins-career': [
        BaseColumnDefinitions.INDEX_COL,
        gmNameColumn,
        BaseColumnDefinitions.TEAM_ABBREVS,
        ...GAMES_WINS_COL(false),
    ],
    'gm-wins-single-franchise': [
        BaseColumnDefinitions.INDEX_COL,
        gmNameColumn,
        FRANCHISE_COL,
        ...GAMES_WINS_COL(false),
    ],
    'gm-wins-career-including-playoffs': [
        BaseColumnDefinitions.INDEX_COL,
        gmNameColumn,
        BaseColumnDefinitions.TEAM_ABBREVS,
        ...GAMES_WINS_COL(false),
    ],
    'gm-wins-single-franchise-including-playoffs': [
        BaseColumnDefinitions.INDEX_COL,
        gmNameColumn,
        FRANCHISE_COL,
        ...GAMES_WINS_COL(false),
    ],
    'gm-playoffs-wins-career': [
        BaseColumnDefinitions.INDEX_COL,
        gmNameColumn,
        BaseColumnDefinitions.TEAM_ABBREVS,
        ...GAMES_WINS_COL(true),
    ],
    'gm-playoffs-single-franchise': [
        BaseColumnDefinitions.INDEX_COL,
        gmNameColumn,
        FRANCHISE_COL,
        ...GAMES_WINS_COL(true),
    ],
    'general-manager-most-seasons-career': GM_CAREER_RECORD_COLS,
    'general-manager-most-seasons-single-franchise': GM_SINGLE_FRANCHISE_RECORD_COLS,
    'general-manager-most-games-career': GM_CAREER_RECORD_COLS,
    'general-manager-most-games-single-franchise': GM_SINGLE_FRANCHISE_RECORD_COLS,
    'general-manager-most-games-career-including-playoffs': GM_CAREER_RECORD_COLS,
    'general-manager-most-games-single-franchise-including-playoffs': GM_SINGLE_FRANCHISE_RECORD_COLS,
    'general-manager-most-years-in-playoffs-career': [...GM_PLAYOFF_CAREER_BASE_COLS, ...GM_GAMES_SEASONS_CUPS_COLS],
    'general-manager-most-years-in-playoffs-single-franchise': [
        ...GM_PLAYOFF_FRANCHISE_BASE_COLS,
        ...GM_GAMES_SEASONS_CUPS_COLS,
    ],
    'general-manager-most-consecutive-years-in-playoffs': [
        ...GM_PLAYOFF_CAREER_BASE_COLS,
        FIRST_SEASON_COL,
        LAST_SEASON_COL,
        CONSECUTIVE_SEASONS,
        CUP_WINS_COL,
    ],
    'general-manager-most-playoff-games-career': [...GM_PLAYOFF_CAREER_BASE_COLS, ...GM_GAMES_SEASONS_CUPS_COLS],
    'general-manager-most-playoff-games-single-franchise': [
        ...GM_PLAYOFF_FRANCHISE_BASE_COLS,
        ...GM_GAMES_SEASONS_CUPS_COLS,
    ],
    'general-manager-most-stanley-cup-final-appearances-career': [
        ...GM_PLAYOFF_CAREER_BASE_COLS,
        SEASONS_COL,
        STANLEY_CUP_FINAL_APPEARANCES,
        CUP_WINS_COL,
    ],
    'general-manager-most-stanley-cup-final-appearances-single-franchise': [
        ...GM_PLAYOFF_FRANCHISE_BASE_COLS,
        SEASONS_COL,
        STANLEY_CUP_FINAL_APPEARANCES,
        CUP_WINS_COL,
    ],
    'general-manager-most-consecutive-stanley-cup-final-appearances': [
        BaseColumnDefinitions.INDEX_COL,
        {
            ...gmNameColumn,
            Header: BaseColumnDefinitions.CreateHeaderCell('General Manager', 'General Manager'),
            accessor: 'generalManagerName',
        },
        BaseColumnDefinitions.TEAM_ABBREVS,
        FIRST_SEASON_COL,
        LAST_SEASON_COL,
        CONSECUTIVE_SEASONS,
        CUP_WINS_COL,
    ],
};
